import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { spaceInvalidations } from "@circle-react/lib/queries/spaces";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { PaginationHelper } from "@circle-react/types";
import type { Topic } from "@circle-react/types/Topic";
import { isEmpty } from "lodash";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const topicKey = createQueryKeyFactory("topic");

export const TOPICS_SETTINGS_PER_PAGE = 20;

interface TopicSettingsPage {
  count?: number;
  has_next_page?: boolean;
  page?: number;
  per_page?: number;
  records: TopicSettingsRecord[];
}

interface TopicSettingsRecord {
  admin_only?: boolean;
  author: {
    avatar_url?: string;
    first_name?: string;
    last_name?: string;
  };
  created_at?: string;
  id?: string;
  name: string;
  space_ids?: number[];
}

interface SettingsListParams {
  adminOnly?: string;
  created_by?: string;
  initialPage?: number;
  order?: Partial<Record<"name" | "created_by" | "created_at", "asc" | "desc">>;
  perPage?: number;
  query?: string;
  spaces?: string;
}

interface TopicPickerParams {
  ids?: number[];
  query?: string;
  spaceId?: string;
}

interface TopicFilterParams {
  isCourseDirectory: boolean;
  spaceId: number | string;
}

interface SpaceTopicsParams {
  spaceId: number | string;
}

interface TopicsResponse {
  records: Topic[];
}

const topicIdsKey = (ids?: number[]) => ids?.join(",") ?? "";

export const topicQueries = {
  settingsList: ({
    adminOnly,
    created_by,
    order,
    query = "",
    perPage = TOPICS_SETTINGS_PER_PAGE,
    initialPage = 1,
    spaces,
  }: SettingsListParams = {}) =>
    infiniteQueryOptions({
      getNextPageParam: lastPage =>
        lastPage.has_next_page && lastPage.page ? lastPage.page + 1 : undefined,
      initialPageParam: initialPage,
      queryKey: topicKey.key(
        "settings",
        query,
        perPage,
        initialPage,
        adminOnly ?? "",
        created_by ?? "",
        JSON.stringify(order),
        spaces ?? "",
      ),
      queryFn: ({ pageParam = initialPage }: { pageParam?: number }) => {
        const page = pageParam > 0 ? pageParam : 1;

        return reactQueryGet<TopicSettingsPage>(
          internalApi.settings.topics.index({
            params: {
              page,
              per_page: perPage,
              ...(query && { query }),
              ...(adminOnly && { admin_only: adminOnly }),
              ...(created_by && { created_by }),
              ...(!isEmpty(order) && { order }),
              ...(spaces && { spaces }),
            },
          }),
        );
      },
    }),
  detail: (id: string | number) =>
    queryOptions({
      queryKey: topicKey.key("detail", id),
      queryFn: () =>
        reactQueryGet<TopicSettingsRecord>(
          internalApi.settings.topics.show({ uuid: id }),
        ),
    }),
  settingsSelectList: ({ perPage = 250 }: { perPage?: number } = {}) =>
    queryOptions({
      queryKey: topicKey.key("settings", "select", perPage),
      queryFn: () =>
        reactQueryGet<PaginationHelper<Topic>>(
          internalApi.settings.topics.index({
            params: {
              per_page: perPage,
            },
          }),
        ),
    }),
  spaceTopics: ({ spaceId }: SpaceTopicsParams) =>
    queryOptions({
      queryKey: topicKey.key("spaces", spaceId),
      queryFn: () =>
        reactQueryGet<TopicsResponse>(
          internalApi.topics.spaceTopics({ spaceId }),
        ),
    }),
  courseTopics: () =>
    queryOptions({
      queryKey: topicKey.key("courses"),
      queryFn: () =>
        reactQueryGet<TopicsResponse>(internalApi.courseTopics.index()),
    }),
  filterList: ({ isCourseDirectory, spaceId }: TopicFilterParams) =>
    queryOptions({
      queryKey: isCourseDirectory
        ? topicKey.key("courses")
        : topicKey.key("spaces", spaceId),
      queryFn: () =>
        isCourseDirectory
          ? reactQueryGet<TopicsResponse>(internalApi.courseTopics.index())
          : reactQueryGet<TopicsResponse>(
              internalApi.topics.spaceTopics({ spaceId }),
            ),
    }),
  communityMemberTopics: (communityMemberId: number) =>
    queryOptions({
      queryKey: topicKey.key("community-member", communityMemberId),
      queryFn: () =>
        reactQueryGet<PaginationHelper<Topic>>(
          internalApi.topics.communityMemberTopics({}),
        ),
    }),
  pickerList: ({ spaceId = "", ids, query = "" }: TopicPickerParams = {}) =>
    queryOptions({
      queryKey: spaceId
        ? topicKey.key("picker", "space", spaceId, topicIdsKey(ids), query)
        : topicKey.key("picker", topicIdsKey(ids), query),
      queryFn: () =>
        reactQueryGet<PaginationHelper<Topic>>(
          spaceId
            ? internalApi.topics.spaceTopics({
                spaceId,
                params: {
                  ...(ids && ids.length > 0 && { ids }),
                  ...(query && { query }),
                },
              })
            : internalApi.settings.topics.index({
                params: {
                  per_page: 100,
                  ...(ids && ids.length > 0 && { ids }),
                  ...(query && { query }),
                },
              }),
        ),
    }),
};

export const topicInvalidations = {
  all: () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: topicKey.root() }),
      spaceInvalidations.all(),
    ]),
  courseTopics: () =>
    queryClient.invalidateQueries({ queryKey: topicKey.key("courses") }),
  detail: (id: string | number) =>
    queryClient.invalidateQueries({ queryKey: topicKey.key("detail", id) }),
  removeDetail: (id: string | number) =>
    queryClient.removeQueries({ queryKey: topicKey.key("detail", id) }),
  spaceTopics: (spaceId: string | number) =>
    queryClient.invalidateQueries({
      queryKey: topicKey.key("spaces", spaceId),
    }),
};
